import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../../components/layout/Header';
import { documentsApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
    FolderOpen, 
    Plus, 
    FileText, 
    Presentation,
    BookOpen,
    Download,
    Trash2,
    ExternalLink,
    Tag,
    Upload,
    Link as LinkIcon,
    File
} from 'lucide-react';

const documentTypes = [
    { value: 'pitch_deck', label: 'Pitch Deck', icon: Presentation },
    { value: 'case_study', label: 'Case Study', icon: FileText },
    { value: 'whitepaper', label: 'Whitepaper', icon: BookOpen },
    { value: 'brochure', label: 'Brochure', icon: FileText },
    { value: 'messaging_guide', label: 'Messaging Guide', icon: FileText },
];

const typeColors = {
    pitch_deck: '#FF3B30',
    case_study: '#0077B5',
    whitepaper: '#34A853',
    brochure: '#F59E0B',
    messaging_guide: '#8B5CF6',
};

export default function DocumentsPage() {
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadType, setUploadType] = useState('file');
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const [newDoc, setNewDoc] = useState({
        name: '',
        type: 'pitch_deck',
        url: '',
        tags: '',
        version: '1.0',
    });

    useEffect(() => {
        loadDocuments();
    }, []);

    const loadDocuments = async () => {
        try {
            const response = await documentsApi.getAll();
            setDocuments(response.data);
        } catch (error) {
            toast.error('Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            if (!newDoc.name) {
                setNewDoc({ ...newDoc, name: file.name.replace(/\.[^/.]+$/, '') });
            }
        }
    };

    const handleCreate = async () => {
        if (!newDoc.name) {
            toast.error('Please enter a document name');
            return;
        }

        if (uploadType === 'url' && !newDoc.url) {
            toast.error('Please enter a URL');
            return;
        }

        if (uploadType === 'file' && !selectedFile) {
            toast.error('Please select a file');
            return;
        }

        setSaving(true);
        try {
            if (uploadType === 'file' && selectedFile) {
                // Upload file
                const formData = new FormData();
                formData.append('file', selectedFile);
                formData.append('name', newDoc.name);
                formData.append('doc_type', newDoc.type);
                formData.append('tags', newDoc.tags);
                formData.append('version', newDoc.version);
                
                await documentsApi.upload(formData);
            } else {
                // URL-based document
                const docData = {
                    ...newDoc,
                    tags: newDoc.tags.split(',').map(t => t.trim()).filter(t => t),
                };
                await documentsApi.create(docData);
            }
            
            toast.success('Document added successfully');
            setDialogOpen(false);
            setNewDoc({ name: '', type: 'pitch_deck', url: '', tags: '', version: '1.0' });
            setSelectedFile(null);
            loadDocuments();
        } catch (error) {
            toast.error('Failed to add document');
        } finally {
            setSaving(false);
        }
    };

    const handleDownload = async (doc) => {
        try {
            const response = await documentsApi.download(doc.id);
            
            if (response.data.file_data) {
                // Download base64 file
                const byteCharacters = atob(response.data.file_data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: response.data.file_type || 'application/octet-stream' });
                
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = response.data.file_name || doc.name;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else if (response.data.url) {
                window.open(response.data.url, '_blank');
            }
        } catch (error) {
            toast.error('Failed to download document');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;
        
        try {
            await documentsApi.delete(id);
            toast.success('Document deleted');
            loadDocuments();
        } catch (error) {
            toast.error('Failed to delete document');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="documents-page">
            <Header 
                title="Documents Repository"
                subtitle="Manage your ABM campaign assets and collateral"
            />

            <div className="p-8">
                {/* Actions */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-2">
                        <FolderOpen className="w-5 h-5 text-brand-red" />
                        <span className="text-content-secondary">
                            {documents.length} document{documents.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="btn-primary gap-2" data-testid="add-document-btn">
                                <Plus className="w-4 h-4" />
                                ADD DOCUMENT
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-surface-card border-line-default max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="text-white font-heading">Add Document</DialogTitle>
                            </DialogHeader>
                            
                            <Tabs value={uploadType} onValueChange={setUploadType} className="mt-4">
                                <TabsList className="bg-surface-app border border-line-default w-full">
                                    <TabsTrigger 
                                        value="file" 
                                        className="flex-1 data-[state=active]:bg-brand-red data-[state=active]:text-white gap-2"
                                    >
                                        <Upload className="w-4 h-4" />
                                        Upload File
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="url" 
                                        className="flex-1 data-[state=active]:bg-brand-red data-[state=active]:text-white gap-2"
                                    >
                                        <LinkIcon className="w-4 h-4" />
                                        URL Link
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="file" className="mt-4 space-y-4">
                                    {/* File Upload */}
                                    <div 
                                        className="border-2 border-dashed border-line-default rounded-lg p-8 text-center hover:border-brand-red/50 transition-colors cursor-pointer"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            onChange={handleFileSelect}
                                            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg"
                                        />
                                        {selectedFile ? (
                                            <div className="flex items-center justify-center gap-3">
                                                <File className="w-8 h-8 text-brand-red" />
                                                <div className="text-left">
                                                    <p className="text-white font-medium">{selectedFile.name}</p>
                                                    <p className="text-xs text-content-muted">
                                                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <Upload className="w-10 h-10 text-content-muted mx-auto mb-3" />
                                                <p className="text-content-secondary">
                                                    Click to upload or drag and drop
                                                </p>
                                                <p className="text-xs text-content-muted mt-1">
                                                    PDF, DOC, PPT, XLS, Images (max 10MB)
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </TabsContent>

                                <TabsContent value="url" className="mt-4 space-y-4">
                                    <div className="space-y-2">
                                        <Label className="text-content-secondary">Document URL *</Label>
                                        <Input
                                            value={newDoc.url}
                                            onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
                                            className="input-base"
                                            placeholder="https://drive.google.com/..."
                                            data-testid="doc-url-input"
                                        />
                                    </div>
                                </TabsContent>
                            </Tabs>

                            <div className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Document Name *</Label>
                                    <Input
                                        value={newDoc.name}
                                        onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                                        className="input-base"
                                        placeholder="Q4 2024 Pitch Deck"
                                        data-testid="doc-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Document Type *</Label>
                                    <Select
                                        value={newDoc.type}
                                        onValueChange={(value) => setNewDoc({ ...newDoc, type: value })}
                                    >
                                        <SelectTrigger className="input-base" data-testid="doc-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-surface-card border-line-default">
                                            {documentTypes.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-content-secondary">Version</Label>
                                        <Input
                                            value={newDoc.version}
                                            onChange={(e) => setNewDoc({ ...newDoc, version: e.target.value })}
                                            className="input-base"
                                            placeholder="1.0"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-content-secondary">Tags (comma-separated)</Label>
                                        <Input
                                            value={newDoc.tags}
                                            onChange={(e) => setNewDoc({ ...newDoc, tags: e.target.value })}
                                            className="input-base"
                                            placeholder="enterprise, saas"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 mt-6">
                                    <Button 
                                        variant="ghost" 
                                        onClick={() => setDialogOpen(false)}
                                        className="btn-ghost"
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        onClick={handleCreate}
                                        disabled={saving}
                                        className="btn-primary"
                                        data-testid="save-doc-btn"
                                    >
                                        {saving ? 'Uploading...' : 'Save Document'}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Documents Grid */}
                {documents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {documents.map((doc) => {
                            const typeConfig = documentTypes.find(t => t.value === doc.type);
                            const Icon = typeConfig?.icon || FileText;
                            const color = typeColors[doc.type] || '#FF3B30';

                            return (
                                <div 
                                    key={doc.id}
                                    className="card-base card-hover p-6"
                                    data-testid={`document-${doc.id}`}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div 
                                            className="p-3 rounded-lg"
                                            style={{ backgroundColor: `${color}20` }}
                                        >
                                            <Icon className="w-6 h-6" style={{ color }} />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {doc.has_file && (
                                                <Badge className="badge badge-success text-xs">File</Badge>
                                            )}
                                            <Badge 
                                                className="text-xs"
                                                style={{ 
                                                    backgroundColor: `${color}20`,
                                                    color: color,
                                                }}
                                            >
                                                v{doc.version}
                                            </Badge>
                                        </div>
                                    </div>

                                    <h3 className="font-medium text-white mb-1 truncate">
                                        {doc.name}
                                    </h3>
                                    <p className="text-sm text-content-muted mb-2">
                                        {typeConfig?.label || doc.type}
                                    </p>
                                    {doc.file_name && (
                                        <p className="text-xs text-content-muted mb-2 truncate">
                                            {doc.file_name}
                                        </p>
                                    )}

                                    {doc.tags && doc.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {doc.tags.map((tag, i) => (
                                                <span 
                                                    key={i}
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-surface-app text-content-secondary"
                                                >
                                                    <Tag className="w-3 h-3" />
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <p className="text-xs text-content-muted mb-4">
                                        Uploaded {formatDate(doc.uploaded_at)}
                                    </p>

                                    <div className="flex gap-2">
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            className="btn-ghost flex-1 gap-2"
                                            onClick={() => handleDownload(doc)}
                                        >
                                            <Download className="w-4 h-4" />
                                            Download
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            className="btn-ghost text-status-error hover:text-status-error"
                                            onClick={() => handleDelete(doc.id)}
                                            data-testid={`delete-doc-${doc.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <FolderOpen className="w-16 h-16 text-content-muted mx-auto mb-4" />
                        <h3 className="text-lg font-heading font-bold text-white mb-2">
                            No Documents Yet
                        </h3>
                        <p className="text-content-secondary mb-6">
                            Upload your first document to get started
                        </p>
                        <Button 
                            onClick={() => setDialogOpen(true)}
                            className="btn-primary gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            ADD DOCUMENT
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
